import { useEffect, useRef } from "react";

// Overlays stack: a menu opens over a side panel, a dialog over the page, a
// select over the dialog. Escape belongs to the one on top, and only to it.
//
// Every overlay registers here instead of listening for Escape itself. One
// document listener, in the bubble phase, hands Escape to the most recently
// opened overlay and marks the event handled (`preventDefault`), so nothing
// underneath closes too. Bubbling last lets a control inside an overlay (a
// calendar, an inline edit) claim Escape first: an event it has already
// `preventDefault`ed is left alone.

interface Layer {
  escape: () => void;
}

const layers: Layer[] = [];

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== "Escape" || event.defaultPrevented || event.isComposing) return;
  const top = layers.at(-1);
  if (!top) return;
  event.preventDefault();
  top.escape();
}

function push(layer: Layer) {
  if (layers.length === 0) document.addEventListener("keydown", onKeyDown);
  layers.push(layer);
  return () => {
    const index = layers.indexOf(layer);
    if (index !== -1) layers.splice(index, 1);
    if (layers.length === 0) document.removeEventListener("keydown", onKeyDown);
  };
}

/** Close this overlay on Escape while it is the top one. */
export function useEscapeLayer(onEscape: () => void, active = true) {
  const onEscapeRef = useRef(onEscape);
  useEffect(() => {
    onEscapeRef.current = onEscape;
  });
  useEffect(() => {
    if (!active) return;
    return push({ escape: () => onEscapeRef.current() });
  }, [active]);
}
